import { useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  CheckIcon,
  LockOpenIcon,
  SparklesIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import { PrimaryButton, SectionLabel, TokenBadge } from './TryoutStep1Upload';
import { TryoutStylePicker } from './TryoutStylePicker';
import { TryoutGeneratingStage } from './TryoutGeneratingStage';
import type { StagingStyle } from './TryoutStep2Configure';
import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics';

// Rotating status lines for the staging (furnishing) generation.
const STAGING_PHASES = [
  'Leerer Raum wird analysiert…',
  'Passende Möbel werden ausgewählt…',
  'Einrichtung wird platziert…',
  'Licht & Materialien werden abgestimmt…',
  'Letzte Details werden verfeinert…',
];

// ============================================================================
// Helpers
// ============================================================================

function downloadBase64(b64: string, filename: string) {
  const bytes = new Uint8Array(atob(b64).split('').map((c) => c.charCodeAt(0)));
  const blob = new Blob([bytes], { type: 'image/png' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function downloadFromUrl(url: string, filename: string) {
  const response = await fetch(url);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ============================================================================
// Transformation slider — one large image with a 3-way toggle
// (Original · Leer · Eingerichtet) overlaid at the bottom. Saves vertical
// space over a triptych and lets the result render big. The staged view keeps
// the estage.ai watermark (the tryout gate).
// ============================================================================

const WatermarkOverlay: React.FC = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    <div
      className="absolute inset-0"
      style={{
        background:
          'repeating-linear-gradient(-45deg, transparent 0px, transparent 56px, rgba(255,255,255,0.08) 56px, rgba(255,255,255,0.08) 58px)',
      }}
    />
    <div className="absolute inset-0 flex items-center justify-center">
      <span
        className="rotate-[-30deg] text-white/30 text-base font-bold tracking-widest whitespace-nowrap select-none"
        style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}
      >
        estage.ai
      </span>
    </div>
  </div>
);

interface TransformationSliderProps {
  originalUrl: string;
  emptyUrl: string;
  stagedUrl: string;
  aspectRatio: '1:1' | '4:3';
}

const TransformationSlider: React.FC<TransformationSliderProps> = ({
  originalUrl,
  emptyUrl,
  stagedUrl,
  aspectRatio,
}) => {
  const slides = [
    { key: 'original', label: 'Original', url: originalUrl, watermark: false },
    { key: 'empty', label: 'Leer', url: emptyUrl, watermark: false },
    { key: 'staged', label: 'Eingerichtet', url: stagedUrl, watermark: true },
  ] as const;
  // Default to the staged result — the payoff — then let the visitor slide back
  // to Leer / Original to see the transformation.
  const [active, setActive] = useState(2);

  return (
    <div
      className="relative overflow-hidden rounded-[20px] ring-1 ring-[#EBEBE6] bg-[#F5F4F0]"
      style={{ aspectRatio: aspectRatio === '1:1' ? '1 / 1' : '4 / 3' }}
    >
      {/* All three stacked + absolute-filled so the frame height never changes
          (original is object-cover to the staging format) and switching is a
          smooth crossfade with no reload. */}
      {slides.map((s, i) => (
        <img
          key={s.key}
          src={s.url}
          alt={s.label}
          className={cn(
            'absolute inset-0 w-full h-full object-cover transition-opacity duration-300',
            i === active ? 'opacity-100' : 'opacity-0',
          )}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
        />
      ))}
      {slides[active].watermark && <WatermarkOverlay />}
      {/* 3-way slider overlay */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full bg-white/90 backdrop-blur-sm p-1 border border-[#EBEBE6] shadow-lg">
        {slides.map((s, i) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setActive(i)}
            aria-pressed={i === active}
            className={cn(
              'px-3.5 h-8 rounded-full text-[12px] font-semibold transition-colors whitespace-nowrap',
              i === active ? 'bg-[#FE6F17] text-white' : 'text-[#5F6B70] hover:text-[#001116]',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Step 3 — Results (staging reveal)
// ============================================================================

interface TryoutStep3ResultsProps {
  previewUrl: string;
  emptyUrl: string;
  stagedUrl: string | null;
  stagedBase64: string | null;
  stagingStyle: StagingStyle;
  roomType: string;
  aspectRatio: '1:1' | '4:3';
  isStaging: boolean;
  isFinishing: boolean;
  stagingError: string | null;
  remaining: number | null;
  limitReached: boolean;
  onStyleChange: (style: StagingStyle) => void;
  onCreateStaging: () => void;
  onReset: () => void;
  onSignup: () => void;
}

export const TryoutStep3Results: React.FC<TryoutStep3ResultsProps> = ({
  previewUrl,
  emptyUrl,
  stagedUrl,
  stagedBase64,
  stagingStyle,
  roomType,
  aspectRatio,
  isStaging,
  isFinishing,
  stagingError,
  remaining,
  limitReached,
  onStyleChange,
  onCreateStaging,
  onReset,
  onSignup,
}) => {
  const handleDownload = () => {
    if (!stagedUrl) return;
    if (stagedBase64) {
      downloadBase64(stagedBase64, `eingerichtet-${roomType}.png`);
    } else {
      void downloadFromUrl(stagedUrl, `eingerichtet-${roomType}.png`);
    }
    track('Tryout Preview Downloaded', { tab: 'staged', room_type: roomType });
  };

  const FEATURES = [
    'Exposé in Minuten erstellen',
    'Höhere Verkaufspreise erzielen',
    'Bilder direkt vom Mieter anfordern',
    '3D Grundriss-Tool inklusive',
    'Unbegrenzte Projekte',
  ];

  return (
    <div className="space-y-6" style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}>
      {isStaging && !stagedUrl ? (
        // ── Generating: keep the visitor engaged with USP bubbles + progress
        //    over the room being furnished (min. display time enforced upstream) ─
        <TryoutGeneratingStage
          imageUrl={emptyUrl}
          originalUrl={emptyUrl}
          aspectRatio={aspectRatio}
          phases={STAGING_PHASES}
          originalLabel="Leer"
          resultLabel="Eingerichtet"
          finished={isFinishing}
        />
      ) : !stagedUrl ? (
        // ── Pre-staging gate: emptied room beside the style choice, so the
        //    "Staging erstellen" button stays in view without scrolling ─────
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-6 items-start">
          {/* Left: the emptied room, height-capped so it never dominates */}
          <div className="relative rounded-[20px] overflow-hidden border border-[#EBEBE6] bg-[#F5F4F0]">
            <img
              src={emptyUrl}
              alt="Leergeräumt"
              className="w-full object-cover max-h-[440px]"
              style={{ aspectRatio: aspectRatio === '1:1' ? '1 / 1' : '4 / 3' }}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>

          {/* Right: style choice + CTA */}
          <div className="space-y-4">
            <div>
              <SectionLabel icon={<SwatchIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />}>
                Einrichtungsstil
              </SectionLabel>
              <TryoutStylePicker value={stagingStyle} onChange={onStyleChange} variant="list" />
            </div>
            <PrimaryButton
              onClick={onCreateStaging}
              disabled={isStaging}
              trailingIcon={<SparklesIcon className="w-5 h-5" strokeWidth={1.75} />}
            >
              {isStaging ? 'Richte ein…' : 'Staging erstellen'}
            </PrimaryButton>
            {stagingError && <p className="text-[12px] text-[#B91C1C] text-center">{stagingError}</p>}
            {remaining !== null && (
              <div className="flex items-center justify-center">
                <TokenBadge remaining={remaining} />
              </div>
            )}
          </div>
        </div>
      ) : (
        // ── Reveal: big transformation slider + signup CTA in the right rail
        //    (try-again + token live only in the right column) ────────────────
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
          {/* Left: one large image, 3-way slider (Original · Leer · Eingerichtet) */}
          <div className="space-y-3">
            <TransformationSlider
              originalUrl={previewUrl}
              emptyUrl={emptyUrl}
              stagedUrl={stagedUrl}
              aspectRatio={aspectRatio}
            />

            <button
              onClick={handleDownload}
              className="w-full inline-flex items-center justify-center gap-2 h-11 px-5 rounded-[10px] bg-white border border-[#EBEBE6] text-[14px] font-semibold text-[#001116] hover:border-[#FE6F17]/50 hover:bg-[#FCFAF5] transition-all duration-150"
              style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}
            >
              <ArrowDownTrayIcon className="w-5 h-5" strokeWidth={1.5} />
              Vorschau herunterladen
            </button>
          </div>

          {/* Right: signup CTA — stays in view */}
          <div className="flex flex-col gap-4">
            <div className="rounded-[20px] bg-gradient-to-br from-[#FFF3EC] to-[#FFE1D1] border border-[#FFD4BF] p-6 space-y-4">
              <div>
                <p className="text-[12px] font-semibold text-[#FE6F17] uppercase tracking-wider mb-1.5">
                  Schon ab 19€
                </p>
                <h3
                  className="text-[22px] leading-[1.25] text-[#001116]"
                  style={{ fontWeight: 600, letterSpacing: '-0.005em' }}
                >
                  Top-Immobilien&shy;bilder ohne Aufwand
                </h3>
              </div>

              <ul className="space-y-2">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-[14px] text-[#001116]">
                    <div className="w-5 h-5 rounded-full bg-[#FE6F17] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>

              <PrimaryButton
                onClick={onSignup}
                trailingIcon={<ArrowRightIcon className="w-5 h-5" strokeWidth={2} />}
              >
                <LockOpenIcon className="w-5 h-5" strokeWidth={1.75} />
                Alle Features freischalten
              </PrimaryButton>
            </div>

            {/* Try again */}
            {!limitReached ? (
              <button
                onClick={() => { onReset(); track('Tryout Try Again Clicked'); }}
                className="w-full inline-flex items-center justify-center gap-2 h-11 px-5 rounded-[10px] bg-white border border-[#EBEBE6] text-[14px] font-semibold text-[#5F6B70] hover:border-[#FE6F17]/50 hover:text-[#001116] transition-all duration-150"
                style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}
              >
                <ArrowPathIcon className="w-4 h-4" strokeWidth={1.75} />
                Erneut probieren
                <span className="text-[12px] font-normal text-[#9CA3AF]">(verbraucht 1 Versuch)</span>
              </button>
            ) : (
              <div className="text-center text-[12px] text-[#9CA3AF] py-2">
                Keine Versuche mehr übrig – registriere dich für unbegrenzte Nutzung
              </div>
            )}

            {remaining !== null && (
              <div className="flex items-center justify-center">
                <TokenBadge remaining={remaining} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
